import { ServiceRequest } from "@features/cx/consumerManagement/types";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Pause,
  Star,
} from "lucide-react";
import { RequestHeaderProps } from "../types/requestHeaderProps";
import { CustomTooltip } from "@shared/components/CustomTooltip";

export function RequestHeader({
  request,
  currentStatus,
  actionButtons,
  action,
}: RequestHeaderProps) {
  const relevantActions = actionButtons.filter(
    (action) => !action.condition || action.condition(currentStatus)
  );

  console.log("RequestHeader Props:", {
    request,
    currentStatus,
    actionButtons,
    action,
  });
  console.log('request on header', request.reason);
  const getStatusBadgeVariant = (status: ServiceRequest["statusDisplay"]) => {
    switch (status) {
      case "approved":
        return "outline";
      case "rejected":
        return "destructive";
      case "pending":
        return "secondary";
      case "completed":
        return "default";
      case "draft":
        return "outline";
      case "submitted":
        return "secondary";
      case "cancelled":
        return "outline";
      case "in_progress":
        return "default";
      case "on hold":
        return "secondary";
      default:
        return "outline";
    }
  };

  const getPriorityClass = (priority: number) => {
    switch (priority) {
      case 0:
        return "bg-amber-100 text-amber-800";
      case 1:
        return "bg-blue-100 text-blue-800";
      case 2:
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const getStatusIcon = (status: ServiceRequest["statusDisplay"]) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-amber-500" />;
      case "submitted":
        return <FileText className="h-4 w-4 text-blue-500" />;
      case "draft":
        return <FileText className="h-4 w-4 text-gray-500" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-700" />;
      case "cancelled":
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "on_hold":
        return <Pause className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <p className="page-title">{request.name}</p>
          {action === "transfer" ? (
            <p className="page-subtitle">
              Submitted on {request.dateRequested}
            </p>
          ) : (
            <p className="text-muted-foreground">
              {request.reason || 'NA'}• Submitted on{" "}
              {request.dateRequested}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              className={action.className}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-blue-500 text-white rounded-full h-16 w-16 flex items-center justify-center text-2xl font-semibold">
            {request.customerName
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="page-subheading">{request.customerName}</p>
              {request.isVip && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                  <Star className="h-3 w-3 mr-1" />
                  VIP
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{request.accountNumber}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="flex items-center gap-1" variant="outline">
                {request.priorityDisplay ?? "NA"}
              </Badge>
              <Badge
                variant={getStatusBadgeVariant(currentStatus)}
                className="flex items-center gap-1"
              >
                {getStatusIcon(currentStatus)}
                {currentStatus.charAt(0).toUpperCase() +
                  currentStatus.slice(1).replace("_", " ")}
              </Badge>
            </div>
          </div>
        </div>

        {/* SLA Information in 3 columns */}
        {request.sla && (
          <div className="grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-md">
            <div className="text-center px-2">
              <p className="text-xs text-muted-foreground flex justify-center items-center gap-2">SLA Status <CustomTooltip iconSize={14} message="Shows 'On Track' if all SLA rules are met, otherwise 'Breached'." /> </p>
              <p className="font-medium flex items-center justify-center">
                <span
                  className={`h-2 w-2 rounded-full mr-1 ${
                    request.sla.status === "On Track"
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                ></span>
                {request.sla.status ?? "NA"}
              </p>
            </div>
            <div className="text-center border-x border-gray-200 px-2">
              <p className="text-xs text-muted-foreground flex items-center gap-2">SLA Due Date <CustomTooltip iconSize={14} message="Deadline calculated from creation date plus defined resolution time." /> </p>
              <p className="font-medium">{request.sla.dueDate ?? "NA"}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground flex items-center gap-2">Time Remaining <CustomTooltip iconSize={14} message="Time left until SLA due date, based on current time." /> </p>
              <p className="font-medium">{request.sla.remaining ?? "NA"}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
